import { useCallback, useMemo } from "react";

import styles from "../styles/EndpointBar.module.css";

/**
 * The endpoint + API key bar that lives at the top of the page. Pure
 * presentational — state and persistence live in App.tsx via
 * localStorage. The API key field is masked but copyable; the
 * "Mostrar / ocultar" toggle is intentionally absent for V1.0 to keep
 * the form noise low. Users can paste the key once and forget about it.
 *
 * Predefined endpoints are surfaced as quick-pick chips. Pasting a
 * custom URL is the escape hatch — typing into the input also fires
 * the change callback so localStorage stays current.
 */
const QUICK_PICKS: readonly {
  readonly label: string;
  readonly value: string;
}[] = [
  { label: "Local (wrangler dev)", value: "http://127.0.0.1:8787" },
  // Staging via the tutellus.dev custom domain (primary).
  { label: "Staging", value: "https://swapgate-api-sta.tutellus.dev" },
  // Workers.dev fallback — useful during DNS propagation or if the
  // custom hostname is misconfigured on a given environment.
  {
    label: "Staging (workers.dev)",
    value: "https://swapgate-api-staging.2telus.workers.dev",
  },
] as const;

export interface EndpointBarProps {
  readonly endpoint: string;
  readonly apiKey: string;
  readonly onEndpointChange: (value: string) => void;
  readonly onApiKeyChange: (value: string) => void;
}

export function EndpointBar(props: EndpointBarProps): React.JSX.Element {
  const { endpoint, apiKey, onEndpointChange, onApiKeyChange } = props;

  const onPickPredefined = useCallback(
    (value: string) => {
      onEndpointChange(value);
    },
    [onEndpointChange],
  );

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onEndpointChange(e.target.value),
    [onEndpointChange],
  );
  const onChangeKey = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onApiKeyChange(e.target.value),
    [onApiKeyChange],
  );

  const isHealthy = useMemo(
    () => endpoint.length > 0 && apiKey.length > 0,
    [endpoint, apiKey],
  );

  return (
    <section className={styles.bar}>
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Endpoint</span>
          <input
            type="url"
            value={endpoint}
            placeholder="http://127.0.0.1:8787"
            onChange={onChangeInput}
            className={styles.input}
            spellCheck={false}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>X-Swapgate-Key</span>
          <input
            type="password"
            value={apiKey}
            placeholder="swg_..."
            onChange={onChangeKey}
            className={styles.input}
            spellCheck={false}
            autoComplete="off"
          />
        </label>

        <span
          className={isHealthy ? styles.dotOk : styles.dotWarn}
          title={isHealthy ? "Ready" : "Endpoint or key missing"}
        />
      </div>

      <div className={styles.chips}>
        {QUICK_PICKS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={endpoint === p.value ? styles.chipActive : styles.chip}
            onClick={() => onPickPredefined(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </section>
  );
}
